import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadIcon from "../../../assets/icons/uploadIcon.svg";

import "./add-template-popup.scss";
import { CONTRACT_ACTION_TYPE } from "../../../utils/constants";

interface AddTemplatePopupProps {
  actionType: string;
  open: boolean;
  templateName: string | undefined;
  fileUrl: string | undefined;
  fileName: string | undefined;
  onCancel: () => void;
  createTemplate: (data: FormData) => void;
  editTemplate: (data: FormData) => void;
  openPdfViewer: (fileURL: string | undefined) => void;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB in bytes
const AddTemplatePopup: React.FC<AddTemplatePopupProps> = ({
  actionType,
  open,
  templateName,
  fileUrl,
  fileName,
  onCancel,
  createTemplate,
  editTemplate,
  openPdfViewer,
}) => {
  const [name, setName] = useState<string>(templateName || "");
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [nameError, setNameError] = useState<string>();
  const [fileError, setFileError] = useState<string>();

  const checkCreateValidation = () => {
    let isValid = true;
    if (!name) {
      isValid = false;
      setNameError("Template name is required");
    } else {
      setNameError("");
    }
    if (!file) {
      setFileError("Template file is required");
      isValid = false;
    } else {
      setFileError("");
    }
    return isValid;
  };

  const checkEditValidation = () => {
    let isValid = true;
    if (!name) {
      isValid = false;
      setNameError("Template name is required");
    } else if (name && name === templateName && !file) {
      isValid = false;
      setNameError("Enter new template name");
    } else {
      setNameError("");
    }
    return isValid;
  };
  const handleSubmit = () => {
    if (
      !checkEditValidation() &&
      actionType === CONTRACT_ACTION_TYPE.EDIT_TEMPLATE
    ) {
      return;
    } else if (
      !checkCreateValidation() &&
      actionType === CONTRACT_ACTION_TYPE.ADD_TEMPLATE
    ) {
      return;
    }
    const formData = new FormData();
    if (file) {
      formData.append("file", file);
    }
    formData.append("name", String(name));
    if (actionType === CONTRACT_ACTION_TYPE.ADD_TEMPLATE) {
      createTemplate(formData);
    } else if (actionType === CONTRACT_ACTION_TYPE.EDIT_TEMPLATE) {
      editTemplate(formData);
    }
  };

  const handleFileChange = (e: any) => {
    setFileError("");
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      setFileError("Only PDF file is allowed");
      e.target.value = null;
      setFile(null);
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setFileError("File size must be less than 10 MB");
      e.target.value = null;
      setFile(null);
      return;
    }

    setFile(file);
    if(file && name === templateName){
      setNameError("")
    }
  };

  useEffect(() => {
    if (!open) {
      setName("");
      setFile(null);
      setFileError("");
      setNameError("");
    }
    if (templateName) {
      setName(templateName);
    }else{
      setName("");
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      slotProps={{
        paper: {
          sx: {
            padding: "0px !important",
            width: "500px",
            maxWidth: "95vw",
            borderRadius: "16px",
            overflowX: "hidden",
          },
        },
      }}
    >
      <DialogTitle className="add-template-title">
        {actionType === CONTRACT_ACTION_TYPE.ADD_TEMPLATE
          ? "Add Contract Template"
          : "Edit Template"}
        <IconButton onClick={onCancel} className="close-icon">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          overflowX: "hidden",
          padding: "10px",
        }}
      >
        <Typography className="input-label">
          Template Name<span className="text-red">*</span>
        </Typography>

        <div className="input-name">
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Enter template name"
            size="small"
            name="providerId"
            className="input-name"
            value={name}
            slotProps={{
              htmlInput: {
                maxLength: 200,
              },
            }}
            onChange={(e) => {
              const value = e.target.value;
              const regex = /^[a-zA-Z0-9!&()\-'. ,]*$/;
              if (regex.test(value)) {
                setName(value);
                setNameError("");
              }
            }}
          />
        </div>
        {nameError && <span className="text-error">{nameError}</span>}

        {actionType === CONTRACT_ACTION_TYPE.EDIT_TEMPLATE && (
          <div className="existing-template-wrapper ">
            <div className="existing-template">Existing Template File:</div>
            <div
              className="clickable-file"
              onClick={() => {
                openPdfViewer(fileUrl);
              }}
            >
              {fileName}
            </div>
          </div>
        )}

        <Typography className="input-label upload-file-label">
          {actionType === CONTRACT_ACTION_TYPE.ADD_TEMPLATE
            ? "Upload Template (PDF)"
            : "Change Template (PDF)"}
          <>
            {actionType === CONTRACT_ACTION_TYPE.ADD_TEMPLATE && (
              <span className="text-red">*</span>
            )}
          </>
        </Typography>

        <div
          className="upload-box"
          onClick={() => fileInputRef.current?.click()}
        >
          <img src={UploadIcon} alt="upload" />
          <span className="click-to-upload">
            {file ? file.name : "Click to upload PDF"}
          </span>
        </div>

        <input
          type="file"
          accept="application/pdf"
          hidden
          ref={fileInputRef}
          onChange={handleFileChange}
        />
        {fileError && <span className="text-error">{fileError}</span>}
      </DialogContent>

      {/* Footer */}
      <DialogActions className="dialog-actions">
        <Button onClick={onCancel} color="inherit" sx={{ borderRadius: "8px" }}>
          Cancel
        </Button>

        <Button variant="contained" className="add-btn" onClick={handleSubmit}>
          {actionType === CONTRACT_ACTION_TYPE.ADD_TEMPLATE
            ? "Add Template"
            : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTemplatePopup;
